import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Settings, SettingsUpdate } from '@lms/shared-types'
import { Card } from '../components/primitives/Card'
import { PageHead } from '../components/primitives/PageHead'
import { Stepper } from '../components/primitives/Stepper'
import { SaveBar } from '../components/primitives/SaveBar'
import { Scope } from '../components/primitives/Scope'
import { EditedBadge } from '../components/primitives/EditedBadge'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { ApiError } from '../api/client'
import { getSettings, updateSettings } from '../api/settings'
import { listTeams, updateTeamCoverage } from '../api/teams'
import { flags } from '../lib/feature-flags'

interface FormState {
  minNoticeDays: number
  escalationLongLeaveDays: number
  birthdayWindowDays: number
  replacementGrantReminderDays: number[]
}

const DEFAULTS: FormState = {
  minNoticeDays: 0,
  escalationLongLeaveDays: 5,
  birthdayWindowDays: 15,
  replacementGrantReminderDays: [14, 7, 1],
}

function fromSettings(s: Settings): FormState {
  return {
    minNoticeDays: s.minNoticeDays,
    escalationLongLeaveDays: s.escalationLongLeaveDays,
    birthdayWindowDays: s.birthdayWindowDays,
    replacementGrantReminderDays: s.replacementGrantReminderDays,
  }
}

function toUpdatePayload(next: FormState, baseline: FormState): SettingsUpdate {
  const out: SettingsUpdate = {}
  if (next.minNoticeDays !== baseline.minNoticeDays)
    out.minNoticeDays = next.minNoticeDays
  if (next.escalationLongLeaveDays !== baseline.escalationLongLeaveDays)
    out.escalationLongLeaveDays = next.escalationLongLeaveDays
  if (next.birthdayWindowDays !== baseline.birthdayWindowDays)
    out.birthdayWindowDays = next.birthdayWindowDays
  if (
    JSON.stringify([...next.replacementGrantReminderDays].sort()) !==
    JSON.stringify([...baseline.replacementGrantReminderDays].sort())
  )
    out.replacementGrantReminderDays = next.replacementGrantReminderDays
  return out
}

export function HrSettingsPage(): JSX.Element {
  const queryClient = useQueryClient()
  const [values, setValues] = useState<FormState>(DEFAULTS)
  const [baseline, setBaseline] = useState<FormState>(DEFAULTS)
  const [error, setError] = useState<string | null>(null)

  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
  })

  useEffect(() => {
    if (!settingsQuery.data) return
    const fresh = fromSettings(settingsQuery.data)
    setValues(fresh)
    setBaseline(fresh)
  }, [settingsQuery.data])

  const changeCount = useMemo(() => {
    let n = 0
    if (values.minNoticeDays !== baseline.minNoticeDays) n++
    if (values.escalationLongLeaveDays !== baseline.escalationLongLeaveDays) n++
    if (values.birthdayWindowDays !== baseline.birthdayWindowDays) n++
    if (
      JSON.stringify([...values.replacementGrantReminderDays].sort()) !==
      JSON.stringify([...baseline.replacementGrantReminderDays].sort())
    )
      n++
    return n
  }, [values, baseline])

  const dirty = changeCount > 0

  const saveMutation = useMutation({
    mutationFn: () => updateSettings(toUpdatePayload(values, baseline)),
    onSuccess: (saved) => {
      const next = fromSettings(saved)
      setValues(next)
      setBaseline(next)
      setError(null)
      // Use setQueryData to avoid a refetch race — the server response is
      // the definitive saved shape so we hydrate the cache directly.
      queryClient.setQueryData(['settings'], saved)
    },
    onError: (err) => {
      const status = err instanceof ApiError ? err.status : 500
      const msg = err instanceof Error ? err.message : 'Save failed'
      setError(`${msg} (${status})`)
    },
  })

  const onReset = (): void => {
    setValues(baseline)
    setError(null)
  }

  return (
    <HrAccessGuard>
      <PageHead
        eyebrow="HR"
        title="Settings"
        subtitle="Global policy knobs that apply company-wide. Changes are audit-logged."
      />

      {settingsQuery.isError ? (
        <div
          role="alert"
          style={{
            marginBottom: 16,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'var(--danger-bg)',
            color: 'var(--danger-fg)',
            border: '1px solid var(--danger-line)',
            fontSize: 13,
          }}
        >
          Couldn’t load current settings, so the fields below show defaults — don’t save until
          this loads, or you may overwrite live values. Refresh to try again.
        </div>
      ) : null}

      {error ? (
        <div
          role="alert"
          style={{
            marginBottom: 16,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'var(--danger-bg)',
            color: 'var(--danger-fg)',
            border: '1px solid var(--danger-line)',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 720 }}>

        {/* ─── Policy section ─────────────────────────────────────────────── */}
        <Card padding={0}>
          <SectionHeader title="Policy" />

          <NumberSettingRow
            label="Minimum advance notice"
            help="Working days required between today and a non-emergency leave's start date. 0 = no minimum."
            unit="days"
            min={0}
            max={60}
            value={values.minNoticeDays}
            baseline={baseline.minNoticeDays}
            scope={{ audience: 'All employees', sensitive: false }}
            onChange={(v) => setValues((prev) => ({ ...prev, minNoticeDays: v }))}
            testId="setting-min-notice-days"
          />

          <NumberSettingRow
            label="Long-leave escalation threshold"
            help="Leave longer than this many working days auto-escalates one level (manager → HOD → director)."
            unit="days"
            min={1}
            max={60}
            value={values.escalationLongLeaveDays}
            baseline={baseline.escalationLongLeaveDays}
            scope={{ audience: 'HR + Managers', sensitive: false }}
            onChange={(v) => setValues((prev) => ({ ...prev, escalationLongLeaveDays: v }))}
            testId="setting-escalation-days"
          />
        </Card>

        {/* ─── Leave types section ─────────────────────────────────────────── */}
        <Card padding={0}>
          <SectionHeader title="Leave types" />

          <SliderSettingRow
            label="Birthday leave window"
            help="Days before/after the employee's birthday during which Birthday leave may be taken. 0 = strict birth-month only."
            unit="days"
            min={0}
            max={31}
            value={values.birthdayWindowDays}
            baseline={baseline.birthdayWindowDays}
            scope={{ audience: 'All employees', sensitive: false }}
            onChange={(v) => setValues((prev) => ({ ...prev, birthdayWindowDays: v }))}
            testId="birthday-window-slider"
          />

          <ReminderWindowsRow
            value={values.replacementGrantReminderDays}
            onChange={(next) =>
              setValues((prev) => ({ ...prev, replacementGrantReminderDays: next }))
            }
          />
        </Card>

        {/* Integration on/off (Slack / Email / Calendar) lives in the
            superadmin /admin → Feature flags panel (org_config-backed and
            actually wired). The old toggles here were unwired duplicates. */}

        {/* ─── Team coverage thresholds section (flag-gated) ───────────────── */}
        {flags.teamCoverageHrControl ? <TeamCoverageSection /> : null}
      </div>

      {/* ─── Sticky save bar (Rule 09) ──────────────────────────────────────── */}
      <SaveBar
        changeCount={changeCount}
        onDiscard={onReset}
        onSave={() => {
          if (dirty && !saveMutation.isPending) saveMutation.mutate()
        }}
        style={{ maxWidth: 720 }}
      />
    </HrAccessGuard>
  )
}

// ─── Team coverage thresholds section ────────────────────────────────────────

/**
 * Renders one row per team allowing HR to adjust the absence cap (slot_pct) inline.
 * Each change fires PATCH /api/v1/admin/teams/{id} immediately and toasts
 * on success/error. The section is only rendered when the
 * `teamCoverageHrControl` feature flag is ON.
 */
function TeamCoverageSection(): JSX.Element {
  const queryClient = useQueryClient()

  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: listTeams,
  })

  const patchMutation = useMutation({
    mutationFn: ({ id, pct }: { id: string; pct: number }) =>
      updateTeamCoverage(id, pct),
    onSuccess: (updated) => {
      queryClient.setQueryData<Awaited<ReturnType<typeof listTeams>>>(
        ['teams'],
        (old) => (old ? old.map((t) => (t.id === updated.id ? updated : t)) : old),
      )
      setToast({ message: 'Coverage threshold saved.', kind: 'ok' })
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Save failed'
      setToast({ message: msg, kind: 'error' })
    },
  })

  const [toast, setToast] = useState<{ message: string; kind: 'ok' | 'error' } | null>(null)

  // Auto-dismiss toast after 3 s.
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 3000)
    return () => clearTimeout(t)
  }, [toast])

  return (
    <Card padding={0} data-testid="team-coverage-section">
      <SectionHeader title="Team coverage thresholds" />

      {toast ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            margin: '8px 20px 0',
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 13,
            background: toast.kind === 'ok' ? 'var(--success-bg)' : 'var(--danger-bg)',
            color: toast.kind === 'ok' ? 'var(--success-fg)' : 'var(--danger-fg)',
            border: `1px solid ${toast.kind === 'ok' ? 'var(--success-line)' : 'var(--danger-line)'}`,
          }}
        >
          {toast.message}
        </div>
      ) : null}

      {teamsQuery.isLoading ? (
        <div
          style={{ padding: '16px 20px', fontSize: 13, color: 'var(--ink-3)' }}
        >
          Loading teams…
        </div>
      ) : null}

      {teamsQuery.isError ? (
        <div
          role="alert"
          style={{ padding: '16px 20px', fontSize: 13, color: 'var(--danger-fg)' }}
        >
          Failed to load teams.
        </div>
      ) : null}

      {teamsQuery.data?.map((team) => (
        <TeamCoverageRow
          key={team.id}
          team={team}
          isPending={patchMutation.isPending && patchMutation.variables?.id === team.id}
          onSave={(pct) => patchMutation.mutate({ id: team.id, pct })}
        />
      ))}
    </Card>
  )
}

interface TeamCoverageRowProps {
  team: { id: string; name: string; slotPct: number }
  isPending: boolean
  onSave: (pct: number) => void
}

function TeamCoverageRow({ team, isPending, onSave }: TeamCoverageRowProps): JSX.Element {
  const [draft, setDraft] = useState<number>(team.slotPct)
  const [inputValue, setInputValue] = useState<string>(String(team.slotPct))
  const edited = draft !== team.slotPct

  // Keep local draft in sync when server refreshes the team.
  useEffect(() => {
    setDraft(team.slotPct)
    setInputValue(String(team.slotPct))
  }, [team.slotPct])

  const commit = (raw: string): void => {
    const n = parseInt(raw, 10)
    if (Number.isNaN(n) || n < 0 || n > 100) {
      // Reset to current server value on invalid input.
      setDraft(team.slotPct)
      setInputValue(String(team.slotPct))
      return
    }
    setDraft(n)
    if (n !== team.slotPct) {
      onSave(n)
    }
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: 16,
        padding: '14px 20px',
        borderBottom: '1px solid var(--line-subtle)',
        background: edited ? 'rgba(61,74,107,0.025)' : undefined,
        opacity: isPending ? 0.6 : 1,
        transition: 'opacity 150ms',
      }}
      data-testid={`coverage-row-${team.id}`}
    >
      <div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
            {team.name}
          </div>
          {edited ? <EditedBadge /> : null}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          Maximum % of the team who may be on leave at the same time.
        </div>
        <Scope audience="HR only" sensitive={false} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          type="number"
          min={0}
          max={100}
          value={inputValue}
          aria-label={`Absence cap for ${team.name}`}
          data-testid={`coverage-input-${team.id}`}
          style={{
            width: 64,
            padding: '4px 8px',
            fontSize: 14,
            fontFamily: 'var(--font-mono)',
            fontWeight: 600,
            textAlign: 'right',
            border: '1px solid var(--line-subtle)',
            borderRadius: 6,
            color: edited ? 'var(--primary)' : 'var(--ink)',
            background: 'var(--surface)',
            accentColor: 'var(--primary)',
          }}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur()
            } else if (e.key === 'Escape') {
              setDraft(team.slotPct)
              setInputValue(String(team.slotPct))
            }
          }}
        />
        <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>%</span>
      </div>
    </div>
  )
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeader({ title }: { title: string }): JSX.Element {
  return (
    <div
      style={{
        padding: '12px 20px 8px',
        borderBottom: '1px solid var(--line-subtle)',
        fontFamily: 'var(--font-mono)',
        fontSize: 9,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: 'var(--ink-3)',
        fontWeight: 700,
      }}
    >
      {title}
    </div>
  )
}

// ─── Number setting row with Stepper ─────────────────────────────────────────

interface ScopeConfig {
  audience: string
  sensitive: boolean
}

interface NumberSettingRowProps {
  label: string
  help: string
  unit: string
  value: number
  baseline: number
  min: number
  max: number
  scope: ScopeConfig
  onChange: (v: number) => void
  testId: string
}

function NumberSettingRow({
  label,
  help,
  unit,
  value,
  baseline,
  min,
  max,
  scope,
  onChange,
  testId,
}: NumberSettingRowProps): JSX.Element {
  const edited = value !== baseline

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'start',
        gap: 16,
        padding: '14px 20px',
        borderBottom: '1px solid var(--line-subtle)',
        background: edited ? 'rgba(61,74,107,0.025)' : undefined,
      }}
    >
      <div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
            {label}
          </div>
          {edited ? <EditedBadge /> : null}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 6, lineHeight: 1.5 }}>
          {help}
        </div>
        <Scope audience={scope.audience} sensitive={scope.sensitive} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 2 }}>
        <Stepper
          value={value}
          {...(edited ? { was: baseline } : {})}
          unit={` ${unit}`}
          onDecrement={() => onChange(Math.max(min, value - 1))}
          onIncrement={() => onChange(Math.min(max, value + 1))}
        />
      </div>
    </div>
  )
}

// ─── Toggle setting row ───────────────────────────────────────────────────────

interface ToggleSettingRowProps {
  label: string
  help: string
  value: boolean
  baseline: boolean
  scope: ScopeConfig
  onChange: (v: boolean) => void
  testId: string
}

function ToggleSettingRow({
  label,
  help,
  value,
  baseline,
  scope,
  onChange,
  testId,
}: ToggleSettingRowProps): JSX.Element {
  const edited = value !== baseline

  return (
    <label
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'start',
        gap: 16,
        padding: '14px 20px',
        borderBottom: '1px solid var(--line-subtle)',
        cursor: 'pointer',
        background: edited ? 'rgba(61,74,107,0.025)' : undefined,
      }}
    >
      <div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
            {label}
          </div>
          {edited ? <EditedBadge /> : null}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 6, lineHeight: 1.5 }}>
          {help}
        </div>
        <Scope audience={scope.audience} sensitive={scope.sensitive} />
      </div>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        style={{ marginTop: 4, width: 18, height: 18, cursor: 'pointer' }}
      />
    </label>
  )
}

// ─── Slider setting row ───────────────────────────────────────────────────────

interface SliderSettingRowProps {
  label: string
  help: string
  unit: string
  value: number
  baseline: number
  min: number
  max: number
  scope: ScopeConfig
  onChange: (v: number) => void
  testId: string
}

function SliderSettingRow({
  label,
  help,
  unit,
  value,
  baseline,
  min,
  max,
  scope,
  onChange,
  testId,
}: SliderSettingRowProps): JSX.Element {
  const edited = value !== baseline

  return (
    <div
      style={{
        padding: '14px 20px',
        borderBottom: '1px solid var(--line-subtle)',
        background: edited ? 'rgba(61,74,107,0.025)' : undefined,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
          {label}
        </div>
        {edited ? <EditedBadge /> : null}
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 6, lineHeight: 1.5 }}>
        {help}
      </div>
      <Scope audience={scope.audience} sensitive={scope.sensitive} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          data-testid={testId}
          aria-label={label}
          style={{ flex: 1, cursor: 'pointer', accentColor: 'var(--primary)' }}
        />
        <span
          style={{
            minWidth: 44,
            textAlign: 'right',
            fontFamily: 'var(--font-mono)',
            fontSize: 14,
            fontWeight: 600,
            color: edited ? 'var(--primary)' : 'var(--ink)',
          }}
        >
          {value} {unit}
        </span>
      </div>
    </div>
  )
}

// ─── Reminder windows chip editor ────────────────────────────────────────────

interface ReminderWindowsRowProps {
  value: number[]
  onChange: (next: number[]) => void
}

function ReminderWindowsRow({ value, onChange }: ReminderWindowsRowProps): JSX.Element {
  const [draft, setDraft] = useState('')
  const add = (): void => {
    const n = Number(draft)
    if (!Number.isInteger(n) || n < 1 || n > 90) return
    if (value.includes(n) || value.length >= 5) return
    onChange([...value, n].sort((a, b) => b - a))
    setDraft('')
  }
  return (
    <div data-testid="setting-reminder-windows" style={{ padding: '12px 16px' }}>
      <div style={{ fontWeight: 600, fontSize: 14 }}>Replacement reminder windows</div>
      <div style={{ fontSize: 13, color: 'var(--muted-fg)', marginBottom: 8 }}>
        Days before a replacement grant expires when employees get a reminder.
        Up to 5 windows, each 1–90 days.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {value.map((d) => (
          <span
            key={d}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              background: 'var(--chip-bg, #eef)',
              fontSize: 13,
            }}
          >
            {d}d
            <button
              type="button"
              aria-label={`Remove ${d} day window`}
              onClick={() => onChange(value.filter((x) => x !== d))}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 14,
                lineHeight: 1,
                minWidth: 20,
                minHeight: 20,
              }}
            >
              ×
            </button>
          </span>
        ))}
        {value.length < 5 ? (
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <input
              type="number"
              min={1}
              max={90}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  add()
                }
              }}
              aria-label="Add reminder window (days)"
              style={{ width: 64, padding: '4px 6px', minHeight: 44 }}
            />
            <button type="button" onClick={add} style={{ minHeight: 44 }}>
              Add
            </button>
          </span>
        ) : null}
      </div>
    </div>
  )
}
